import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Dialog } from 'radix-ui';
import { formatCopy } from '@/i18n/ns';
import { OverflowMenu, type OverflowItem } from '@/components/ui/menu';
import { WorkspaceIcon } from '../icons';
import {
  ConnectionAccessForm,
  ConnectionReach,
  type AccessAction,
  type ConnectionToolLabel,
} from '../connect-access/policy-sheet';
import type { ConnectionReadItem } from '../connection-read-access/api';
import { ConnectionMark } from './connection-mark';
import { PanelActivity } from './panel-activity';
import { rowIdentity, type ConnectionWords } from './words';
import type { ConnectionRow } from './rows';

export type PanelTab = 'overview' | 'access' | 'activity';

// When the row that opened the panel is gone (removed, or the page moved on to Activity), focus
// lands on the page's own heading instead of the document body.
function focusPageHeading() {
  const target =
    document.getElementById('ws-conn-yours-title') ??
    document.querySelector<HTMLElement>('.ws-connections-page h1');
  if (!target) return;
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

/** What the panel can do for the row's own source; each page section supplies its actions. */
export interface PanelController {
  busy: boolean;
  /** Sign in again (reconnect or authorize), when the source allows it. */
  signIn?: () => void;
  /** Connect a second account of the same app (another mailbox, another store). */
  addAnother?: () => void;
  test?: () => void;
  /** Remove through the access form's confirmation (connections with their own access). */
  remove?: (fetchImpl?: typeof fetch) => Promise<void>;
  removeMessage?: string;
  /** Disconnect an account without its own access settings. */
  disconnect?: () => Promise<void>;
  onSaved: (next: ConnectionReadItem) => void;
  onStale: () => void;
  tools?: ConnectionToolLabel[];
  /** Why the last action failed, said once on the panel it belongs to. */
  problem?: string | null;
  /** Shown on Overview: the account's own status detail (readiness, last error). */
  detail?: string | null;
  /** Source-specific controls on Overview, such as a database's time limit. */
  extra?: ReactNode;
}

/**
 * One connection, beside the page: what Poneva can do with it, who can use it and what it did.
 * A side panel on the dialog layer, so the list stays in view and Escape or the scrim closes it.
 */
export function ConnectionPanel({
  row,
  controller,
  words,
  logo,
  companyName,
  initialTab = 'overview',
  initialAction = null,
  canViewActivity,
  feedback,
  onClose,
  onTryInChat,
  onOpenActivity,
}: {
  row: ConnectionRow;
  controller: PanelController;
  words: ConnectionWords;
  logo: string | null | undefined;
  companyName: string;
  initialTab?: PanelTab;
  initialAction?: AccessAction | null;
  canViewActivity: boolean;
  /** The page's notices while the panel covers the page: test results, sign-in links, failures. */
  feedback?: ReactNode;
  onClose: () => void;
  onTryInChat?: (prompt: string) => void;
  onOpenActivity?: () => void;
}) {
  const { copy } = words;
  const read = row.read;
  const tabs: PanelTab[] = [
    'overview',
    ...(read ? (['access'] as const) : []),
    ...(read && canViewActivity ? (['activity'] as const) : []),
  ];
  const [tab, setTabState] = useState<PanelTab>(
    tabs.includes(initialTab) ? initialTab : 'overview',
  );
  const opener = useRef<Element | null>(document.activeElement);
  const [action, setAction] = useState<AccessAction | null>(initialAction);
  // Each Turn off or Remove from the menu asks again, also while Access is already open.
  const [actionRequest, setActionRequest] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const setTab = (next: PanelTab, nextAction: AccessAction | null = null) => {
    setAction(nextAction);
    if (nextAction) setActionRequest((count) => count + 1);
    setTabState(next);
  };
  const identity = rowIdentity(row, words);
  const connectedOn = words.date(row.connectedAt);
  const lastUsed = words.ago(row.lastUsedAt);
  const menu: OverflowItem[] = [];
  if (controller.test)
    menu.push({
      key: 'test',
      label: words.access.testConnection,
      disabled: controller.busy,
      onSelect: controller.test,
    });
  if (controller.signIn)
    menu.push({
      key: 'sign-in',
      label: copy.signInAgain,
      disabled: controller.busy,
      onSelect: controller.signIn,
    });
  if (controller.addAnother)
    menu.push({
      key: 'add-another',
      label: copy.addAccount,
      disabled: controller.busy,
      onSelect: controller.addAnother,
    });
  if (read?.canSet && read.access.enabled)
    menu.push({
      key: 'off',
      label: words.access.turnOff,
      tone: 'danger',
      separated: menu.length > 0,
      onSelect: () => setTab('access', 'off'),
    });
  if (read?.canSet && controller.remove)
    menu.push({
      key: 'remove',
      label: words.access.remove,
      tone: 'danger',
      separated: menu.length > 0 && !(read.access.enabled && read.canSet),
      onSelect: () => setTab('access', 'remove'),
    });
  const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.indexOf(tab);
    const next =
      event.key === 'ArrowRight'
        ? tabs[(at + 1) % tabs.length]
        : event.key === 'ArrowLeft'
          ? tabs[(at - 1 + tabs.length) % tabs.length]
          : null;
    if (!next) return;
    event.preventDefault();
    setTab(next);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-tab="${next}"]`)?.focus();
  };
  const tabLabel: Record<PanelTab, string> = {
    overview: copy.tabOverview,
    access: copy.tabAccess,
    activity: copy.tabActivity,
  };
  return (
    <Dialog.Root open onOpenChange={(open) => !open && !busy && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="ws-conn-panel-scrim" />
        <Dialog.Content
          className="ws-portal ws-conn-panel"
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            if (opener.current?.isConnected) return;
            event.preventDefault();
            focusPageHeading();
          }}
        >
          <div className="ws-conn-panel-top">
            <OverflowMenu
              label={formatCopy(copy.moreActions, { name: row.name })}
              items={menu}
              variant="quiet"
            />
            <Dialog.Close className="ws-conn-icon-button" aria-label={copy.close} disabled={busy}>
              <WorkspaceIcon name="close" size={17} />
            </Dialog.Close>
          </div>
          <header className="ws-conn-panel-hero">
            <ConnectionMark
              name={row.name}
              src={row.appSlug ? logo : null}
              logoId={row.logoId}
              glyph={row.logoId === 'custom-mcp' ? 'source' : undefined}
              state={row.state}
              size="xl"
            />
            <div>
              <Dialog.Title className="ws-conn-panel-title">{row.name}</Dialog.Title>
              {identity && <p className="ws-conn-panel-identity">{identity}</p>}
              <p className="ws-conn-panel-status">
                <span className="ws-conn-state" data-state={row.state}>
                  {words.state(row.state)}
                </span>
                {connectedOn && <span>{formatCopy(copy.connectedOn, { date: connectedOn })}</span>}
                {lastUsed && <span>{formatCopy(copy.lastUsed, { when: lastUsed })}</span>}
              </p>
            </div>
          </header>
          {feedback && <div className="ws-conn-panel-feedback">{feedback}</div>}
          {tabs.length > 1 && (
            <div className="ws-conn-tabs" role="tablist" aria-label={row.name} onKeyDown={onTabKey}>
              {tabs.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  data-tab={id}
                  id={`ws-conn-tab-${id}`}
                  aria-selected={tab === id}
                  aria-controls={`ws-conn-tabpanel-${id}`}
                  tabIndex={tab === id ? 0 : -1}
                  disabled={busy}
                  onClick={() => setTab(id)}
                >
                  {tabLabel[id]}
                </button>
              ))}
            </div>
          )}
          {tab === 'access' && read ? (
            <div
              className="ws-conn-panel-form"
              role="tabpanel"
              id="ws-conn-tabpanel-access"
              aria-labelledby="ws-conn-tab-access"
            >
              <ConnectionAccessForm
                key={actionRequest}
                item={read}
                companyName={companyName}
                tools={controller.tools}
                initialAction={action}
                removeMessage={controller.removeMessage}
                onRemove={controller.remove}
                onSaved={controller.onSaved}
                onStale={controller.onStale}
                showReach={false}
                onBusyChange={setBusy}
                onDone={(outcome) => {
                  if (outcome === 'removed') return onClose();
                  setNotice(outcome === 'saved' ? words.access.saved : null);
                  setTab('overview');
                }}
              />
            </div>
          ) : (
            <div
              className="ws-conn-panel-body"
              role={tabs.length > 1 ? 'tabpanel' : undefined}
              id={`ws-conn-tabpanel-${tab}`}
              aria-labelledby={tabs.length > 1 ? `ws-conn-tab-${tab}` : undefined}
            >
              {tab === 'activity' && read ? (
                <PanelActivity
                  connectionId={read.connectionId}
                  words={words}
                  onOpenAll={onOpenActivity}
                />
              ) : (
                <PanelOverview
                  row={row}
                  controller={controller}
                  words={words}
                  notice={notice}
                  onAccess={(next) => setTab('access', next)}
                  onTryInChat={onTryInChat}
                  onDisconnected={onClose}
                />
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelOverview({
  row,
  controller,
  words,
  notice,
  onAccess,
  onTryInChat,
  onDisconnected,
}: {
  row: ConnectionRow;
  controller: PanelController;
  words: ConnectionWords;
  notice: string | null;
  onAccess: (action: AccessAction | null) => void;
  onTryInChat?: (prompt: string) => void;
  onDisconnected: () => void;
}) {
  const { copy, access } = words;
  const read = row.read;
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const readiness = row.item?.readiness;
  const ready =
    readiness?.state === 'ready'
      ? readiness.accountCount !== undefined
        ? words.count(copy.adAccountsNoun, readiness.accountCount)
        : readiness.toolCount !== undefined
          ? words.count(copy.toolsNoun, readiness.toolCount)
          : null
      : null;
  const changes = !read
    ? copy.changesNever
    : read.supportsWrites === false
      ? copy.changesNever
      : row.changes === 'write'
        ? copy.changesAllowed
        : copy.changesAsk;
  const reach = read && (read.provider === 'postgres' || Boolean(controller.tools?.length));
  return (
    <>
      {notice && (
        <p className="ws-conn-panel-notice" role="status">
          {notice}
        </p>
      )}
      {controller.problem && (
        <p className="ws-conn-callout" data-tone="warn" role="status">
          {controller.problem}
        </p>
      )}
      <StateCallout row={row} controller={controller} words={words} onAccess={onAccess} />
      {row.changes === 'unknown' && (
        // A server whose access could not be read: nothing is claimed about what it may do.
        <div className="ws-conn-callout" data-tone="warn" role="status">
          <p>{copy.accessUnknown}</p>
          <div className="ws-conn-callout-actions">
            <button type="button" className="ws-conn-btn" onClick={controller.onStale}>
              {copy.retry}
            </button>
          </div>
        </div>
      )}
      {row.changes !== 'unknown' && (
        <section className="ws-conn-panel-section" aria-labelledby="ws-conn-what">
          <h3 id="ws-conn-what">{copy.whatTitle}</h3>
          <div className="ws-conn-box">
            <div className="ws-conn-line">
              <WorkspaceIcon name="eye" size={15} />
              <span>{read ? copy.readsLine : row.item?.description}</span>
              {ready && <small>{formatCopy(copy.readinessReady, { count: ready })}</small>}
            </div>
            <div className="ws-conn-line">
              <WorkspaceIcon name="edit" size={15} />
              <span>{changes}</span>
            </div>
            {reach && read && (
              <div className="ws-conn-reach">
                <h4>{access.reach}</h4>
                {read.provider === 'postgres' ? (
                  <ul className="ws-conn-pills">
                    {read.resources
                      .filter((resource) => read.access.resources.includes(resource.id))
                      .map((resource) => (
                        <li key={resource.id}>{resource.label}</li>
                      ))}
                  </ul>
                ) : (
                  <ConnectionReach item={read} tools={controller.tools} />
                )}
              </div>
            )}
          </div>
        </section>
      )}
      <section className="ws-conn-panel-section" aria-labelledby="ws-conn-access">
        <h3 id="ws-conn-access">{copy.tabAccess}</h3>
        <div className="ws-conn-box">
          {read ? (
            <>
              <div className="ws-conn-kv">
                <div>
                  <span>{access.qWho}</span>
                  <strong>{words.who(row)}</strong>
                </div>
                <button type="button" className="ws-text-button" onClick={() => onAccess(null)}>
                  {read.canSet ? copy.change : access.cardViewAccess}
                </button>
              </div>
              <div className="ws-conn-kv">
                <div>
                  <span>{access.qWhat}</span>
                  <strong>{words.what(row)}</strong>
                </div>
                <button type="button" className="ws-text-button" onClick={() => onAccess(null)}>
                  {read.canSet ? copy.change : access.cardViewAccess}
                </button>
              </div>
            </>
          ) : (
            <div className="ws-conn-kv">
              <div>
                <span>{access.qWho}</span>
                <strong>
                  {row.audience === 'private'
                    ? copy.nativeOnlyYou
                    : formatCopy(copy.nativeEveryone, { company: words.company })}
                </strong>
              </div>
            </div>
          )}
        </div>
        <p className="ws-conn-note">{copy.providerPermissions}</p>
      </section>
      {controller.extra}
      {onTryInChat && row.state === 'connected' && (
        <section className="ws-conn-panel-section" aria-labelledby="ws-conn-try">
          <h3 id="ws-conn-try">{copy.tryTitle}</h3>
          <button
            type="button"
            className="ws-conn-prompt"
            onClick={() => onTryInChat(formatCopy(copy.tryPrompt, { name: row.name }))}
          >
            {formatCopy(copy.tryPrompt, { name: row.name })}
            <WorkspaceIcon name="arrow" size={15} />
          </button>
        </section>
      )}
      {controller.disconnect && (
        <section className="ws-conn-panel-section">
          {confirmDisconnect ? (
            <div className="ws-conn-callout" data-tone="danger" role="alert">
              <p>{formatCopy(copy.disconnectConfirm, { name: row.name })}</p>
              <div className="ws-conn-callout-actions">
                <button
                  type="button"
                  className="ws-conn-btn"
                  data-tone="danger"
                  disabled={disconnecting || controller.busy}
                  onClick={() => {
                    setDisconnecting(true);
                    void controller
                      .disconnect?.()
                      .then(onDisconnected)
                      .catch(() => undefined)
                      .finally(() => setDisconnecting(false));
                  }}
                >
                  {copy.disconnect}
                </button>
                <button
                  type="button"
                  className="ws-conn-btn"
                  disabled={disconnecting}
                  onClick={() => setConfirmDisconnect(false)}
                >
                  {copy.disconnectKeep}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="ws-text-button ws-conn-danger-link"
              disabled={controller.busy}
              onClick={() => setConfirmDisconnect(true)}
            >
              {copy.disconnect}
            </button>
          )}
        </section>
      )}
    </>
  );
}

function StateCallout({
  row,
  controller,
  words,
  onAccess,
}: {
  row: ConnectionRow;
  controller: PanelController;
  words: ConnectionWords;
  onAccess: (action: AccessAction | null) => void;
}) {
  const { copy, access } = words;
  if (row.state === 'connected')
    return controller.detail ? <p className="ws-conn-note">{controller.detail}</p> : null;
  const text =
    controller.detail ??
    formatCopy(
      {
        attention: copy.attentionBody,
        review: copy.reviewBody,
        unavailable: copy.unavailableBody,
        off: copy.offBody,
      }[row.state],
      { name: row.name },
    );
  const button =
    row.state === 'attention' && controller.signIn ? (
      <button type="button" className="ws-conn-btn" data-tone="warn" onClick={controller.signIn}>
        {copy.signInAgain}
      </button>
    ) : row.state === 'off' && row.read?.canSet ? (
      <button type="button" className="ws-conn-btn" onClick={() => onAccess('on')}>
        {access.turnOn}
      </button>
    ) : row.state === 'review' ? (
      <button type="button" className="ws-conn-btn" onClick={() => onAccess(null)}>
        {copy.stateReview}
      </button>
    ) : row.state === 'unavailable' && controller.test ? (
      <button type="button" className="ws-conn-btn" onClick={controller.test}>
        {access.testConnection}
      </button>
    ) : null;
  return (
    <div
      className="ws-conn-callout"
      data-tone={row.state === 'off' ? undefined : 'warn'}
      role="status"
    >
      <p>{text}</p>
      {button && <div className="ws-conn-callout-actions">{button}</div>}
    </div>
  );
}
